/**
 * KYB onboarding constants. Values must match the backend enums exactly
 * (src/enums/CompanyEntityType.ts, CompanyGstStatus.ts, CompanyDocumentType.ts) —
 * they are sent as-is and validated server-side with z.nativeEnum.
 */

export const ENTITY_TYPES = [
  'Private Limited',
  'Public Limited',
  'LLP',
  'Partnership',
  'Proprietorship',
  'Trust',
  'Society',
  'HUF'
];

export const ENTITY_TYPE_OPTIONS = [
  { value: '', label: 'Select', disabled: true, selected: true },
  ...ENTITY_TYPES.map((value) => ({ value, label: value }))
];

export const GST_STATUS = {
  REGISTERED: 'registered',
  NOT_REGISTERED: 'not_registered'
};

export const GST_STATUS_OPTIONS = [
  { value: GST_STATUS.REGISTERED, label: 'GST registered' },
  { value: GST_STATUS.NOT_REGISTERED, label: 'Not GST registered' }
];

/**
 * Formation proof accepted per entity type — mirrors FORMATION_PROOF_BY_ENTITY_TYPE in
 * the backend. Used to tell the user which document they will need to upload; the
 * backend policy service is what actually decides whether the requirement is met.
 */
export const FORMATION_PROOF_BY_ENTITY_TYPE = {
  'Private Limited': ['Certificate of Incorporation'],
  'Public Limited': ['Certificate of Incorporation'],
  LLP: ['Certificate of Incorporation'],
  Partnership: ['Partnership deed or registration certificate'],
  Proprietorship: [
    'Shop & Establishment certificate',
    'Udyam registration',
    'Signed self-declaration'
  ],
  Trust: ['Trust deed'],
  Society: ['Trust deed', 'Registration certificate'],
  HUF: ['Signed self-declaration']
};

/**
 * Document types accepted by POST /company/:id/documents. `value` must match the backend
 * CompanyDocumentType enum exactly.
 */
export const DOCUMENT_CATALOGUE = [
  {
    value: 'incorporation_certificate',
    label: 'Certificate of Incorporation',
    description: 'Confirms entity type, directors, and date of incorporation.'
  },
  {
    value: 'partnership_deed',
    label: 'Partnership Deed',
    description: 'Deed or registration certificate for a partnership firm.'
  },
  {
    value: 'gst_certificate',
    label: 'GST Certificate',
    description: 'Optional. Shows tax details, registered address, and legal name.'
  },
  {
    value: 'shop_establishment',
    label: 'Shop & Establishment Certificate',
    description: 'State registration for a place of business.'
  },
  {
    value: 'udyam',
    label: 'Udyam Registration',
    description: 'MSME registration certificate.'
  },
  { value: 'trust_deed', label: 'Trust Deed', description: 'Deed or registration certificate.' },
  {
    value: 'self_declaration',
    label: 'Self-Declaration',
    description: 'Signed declaration where no registration document exists.'
  },
  { value: 'pan_card', label: 'PAN Card', description: 'Required. Business PAN for identity verification.' }
];

/** Backend doc_type values that satisfy the formation proof for an entity type. */
export const FORMATION_PROOF_TYPES_BY_ENTITY_TYPE = {
  'Private Limited': ['incorporation_certificate'],
  'Public Limited': ['incorporation_certificate'],
  LLP: ['incorporation_certificate'],
  Partnership: ['partnership_deed'],
  Proprietorship: ['shop_establishment', 'udyam', 'self_declaration'],
  Trust: ['trust_deed'],
  Society: ['trust_deed', 'incorporation_certificate'],
  HUF: ['self_declaration']
};

export function formationProofTypesFor(entityType) {
  return FORMATION_PROOF_TYPES_BY_ENTITY_TYPE[entityType] ?? [];
}

/** Always required identity document — GST certificate is optional supporting evidence. */
export const REQUIRED_IDENTITY_DOC_TYPES = ['pan_card'];

/**
 * Best-effort default entity type from the legacy free-text company type, so the user is
 * not asked the same question twice. Ambiguous values (Joint Venture) map to nothing and
 * the user picks explicitly.
 */
/**
 * Reverse of the above: when Smart Fill determines the legal entity type from a document,
 * pre-select the matching legacy company type so the operator is not asked the same
 * question twice. Trust and HUF have no legacy equivalent and are left for manual choice.
 */
export const COMPANY_TYPE_FROM_ENTITY_TYPE = {
  Proprietorship: 'Sole Proprietorship',
  Partnership: 'Partnership',
  LLP: 'Limited Liability (LLC)',
  'Public Limited': 'Public Limited (Ltd)',
  'Private Limited': 'Private Limited (Pvt Ltd)',
  Society: 'Nonprofit Organization'
};

export const ENTITY_TYPE_FROM_COMPANY_TYPE = {
  'Sole Proprietorship': 'Proprietorship',
  Partnership: 'Partnership',
  'Limited Liability (LLC)': 'LLP',
  'Public Limited (Ltd)': 'Public Limited',
  'Private Limited (Pvt Ltd)': 'Private Limited',
  'Nonprofit Organization': 'Society',
  'Co-operative': 'Society'
};
